import {
  FilterDropdownInfo,
  FilterDropdownInfoCategories,
  FilterDropdownItem,
  FilterHistory,
} from '@/common/components/molecules/filterSearch/filterSearch.type';
import { computed } from 'vue';
import { useInternational } from '@/common/locale';
import { getElapsedTime } from '@/common/utils/commonUtils';
import { MAXIMUM_HISTORY_SIZE } from '@/common/components/molecules/filterSearch/filterSearch.define';

export interface Props {
  dropdownListInfo: FilterDropdownInfo;
  hoveredDropdownItemIndex: number;
  showFilterHistoryList: boolean;
  filterHistory: FilterHistory[];
  searchText: string;
}

interface HighlightPart {
  text: string;
  isMatch: boolean;
}

export interface Emit {
  (e: 'mouseenter-to-item', index: number): void;
  (e: 'click-list-item', value: FilterDropdownItem): void;
  (e: 'click-history-item', value: FilterHistory): void;
  (e: 'remove-history-item', value: FilterHistory): void;
}

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();

  const getDropdownListTitle = (category: FilterDropdownInfoCategories): string => {
    switch (category) {
      case 'key':
        return t('WORD.FILTER_CATEGORY');

      case 'operator':
        return t('WORD.FILTER_INEQUALITY_SIGN');

      case 'value':
        return t('WORD.FILTER_VALUE');

      default:
        return '';
    }
  };

  const onMouseenter = (index: number) => {
    emit('mouseenter-to-item', index);
  };

  const onClickListItem = (target: FilterDropdownItem) => {
    emit('click-list-item', target);
  };

  const onClickHistoryItem = (target: FilterHistory) => {
    emit('click-history-item', target);
  };

  const onClickRemoveHistoryItem = (e: MouseEvent, target: FilterHistory) => {
    e.stopPropagation();
    emit('remove-history-item', target);
  };

  const filterHistoryList = computed(() =>
    props.filterHistory?.slice()?.reverse()?.slice(0, MAXIMUM_HISTORY_SIZE),
  );

  const getHighlightParts = (name: string, search: string): HighlightPart[] => {
    if (!search?.trim()) return [{ text: name, isMatch: false }];
    const lowerName = name.toLowerCase();
    const lowerSearch = search.toLowerCase().trim();
    const index = lowerName.indexOf(lowerSearch);
    if (index === -1) return [{ text: name, isMatch: false }];

    const parts: HighlightPart[] = [];
    if (index > 0) parts.push({ text: name.slice(0, index), isMatch: false });
    parts.push({ text: name.slice(index, index + lowerSearch.length), isMatch: true });
    const rest = index + lowerSearch.length;
    if (rest < name.length) parts.push({ text: name.slice(rest), isMatch: false });
    return parts;
  };

  const getElapsedTimeSinceHistoryCreated = (createdTime: number): string => {
    const { time, unit } = getElapsedTime(createdTime);

    let result: string = '';
    if (time && unit) {
      const plural = time > 1;
      result = t('WORD.ELAPSED_TIME_FROM_NOW', {
        time,
        unit: plural ? t('WORD.PLURAL_ELAPSED_TIME_UNIT', { value: unit }) : unit,
      });
    } else if (!time && !unit) {
      result = `${t('WORD.JUST_NOW')}`;
    }

    return result;
  };

  return {
    t,
    filterHistoryList,
    getDropdownListTitle,
    getHighlightParts,
    getElapsedTimeSinceHistoryCreated,
    onMouseenter,
    onClickListItem,
    onClickHistoryItem,
    onClickRemoveHistoryItem,
  };
};
